// Payroll - Operational Costs Tab (with Budget Code, FY, Funding Source, Supporting Docs File Upload & Operational Cost Report Generator)
import React, { useState, useEffect } from 'react';
import { hasAnyRole } from '../../utils/roleHelper';

const STATUS_COLORS = {
  pending:  { bg: '#fef9c3', text: '#854d0e', label: 'Pending Approval' },
  active:   { bg: '#dcfce7', text: '#166534', label: 'Active / Approved' },
  inactive: { bg: '#f1f5f9', text: '#475569', label: 'Inactive / Rejected' },
};

const Badge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.text }}>{s.label}</span>;
};

const OperationalCostsTab = ({ api, user, armyTypeId, armyTypeName }) => {
  const [costs, setCosts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({
    name: '', cost_type: 'fixed', amount: '', frequency: 'monthly', department_id: '',
    description: '', budget_code: '', financial_year: '2026', funding_source: '', supporting_document_path: ''
  });
  const [docFileName, setDocFileName] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [filter, setFilter] = useState('all');

  // Approval History Modal state
  const [historyModal, setHistoryModal] = useState({ open: false, loading: false, data: null });

  // Report Modal state
  const [reportModal, setReportModal] = useState({ open: false, filterStatus: 'all', filterFy: '2026', filterDept: '' });

  // View Document Modal state
  const [docModal, setDocModal] = useState({ open: false, title: '', url: '' });

  const canManage = hasAnyRole(user, ['admin', 'finance_officer', 'finance_director', 'finance']);
  const canApprove = hasAnyRole(user, ['admin', 'finance_director', 'accountant_general']);

  useEffect(() => { fetchData(); }, [armyTypeId]);

  const fetchData = async () => {
    try {
      const [cRes, dRes] = await Promise.all([
        api.get(`/operational_costs?army_type_id=${armyTypeId}`),
        api.get('/departments')
      ]);
      setCosts(cRes?.operational_costs || cRes?.data?.operational_costs || []);
      setDepartments(Array.isArray(dRes) ? dRes : (dRes?.departments || dRes?.data || []));
    } catch (e) { console.error(e); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      alert('File size exceeds 15MB limit.');
      e.target.value = '';
      return;
    }

    setUploadingDoc(true);
    setDocFileName(file.name);

    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({
        ...prev,
        supporting_document_path: reader.result
      }));
      setUploadingDoc(false);
    };
    reader.onerror = () => {
      alert('Failed to read file');
      setUploadingDoc(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveDoc = () => {
    setForm(prev => ({ ...prev, supporting_document_path: '' }));
    setDocFileName('');
    const fileInput = document.getElementById('supporting-doc-file-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setMsg({ text: '', type: '' });
    try {
      const payload = { operational_cost: { ...form, army_type_id: armyTypeId } };
      let res;
      if (editing) {
        res = await api.put(`/operational_costs/${editing}`, payload);
      } else {
        res = await api.post('/operational_costs', payload);
      }
      if (res?.response && !res.response.ok) {
        setMsg({ text: res.data?.errors?.join(', ') || res.data?.error || 'Error saving cost', type: 'error' });
      } else {
        setMsg({ text: editing ? '✅ Operational cost updated successfully.' : '✅ Cost submitted for Finance approval.', type: 'success' });
        setForm({
          name: '', cost_type: 'fixed', amount: '', frequency: 'monthly', department_id: '',
          description: '', budget_code: '', financial_year: '2026', funding_source: '', supporting_document_path: ''
        });
        setDocFileName('');
        const fileInput = document.getElementById('supporting-doc-file-input');
        if (fileInput) fileInput.value = '';
        setEditing(null);
        fetchData();
      }
    } catch (e) { setMsg({ text: 'Error saving cost', type: 'error' }); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id, name) => {
    try {
      const res = await api.patch(`/operational_costs/${id}/approve`);
      if (res?.response && !res.response.ok) {
        setMsg({ text: res.data?.error || 'Error approving', type: 'error' });
      } else {
        setMsg({ text: `✅ "${name}" approved and activated`, type: 'success' });
        fetchData();
      }
    } catch (e) { setMsg({ text: 'Error approving cost', type: 'error' }); }
  };

  const handleReject = async (id, name) => {
    const reason = prompt(`Reason for rejecting "${name}"?`);
    if (!reason) return;
    try {
      const res = await api.patch(`/operational_costs/${id}/reject`, { reason });
      if (res?.response && !res.response.ok) {
        setMsg({ text: res.data?.error || 'Error rejecting', type: 'error' });
      } else {
        setMsg({ text: `❌ "${name}" rejected`, type: 'success' });
        fetchData();
      }
    } catch (e) { setMsg({ text: 'Error rejecting cost', type: 'error' }); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this cost?')) return;
    try {
      const res = await api.delete(`/operational_costs/${id}`);
      if (res?.response && !res.response.ok) {
        setMsg({ text: res.data?.error || 'Could not delete cost', type: 'error' });
      } else {
        fetchData();
      }
    } catch (e) { setMsg({ text: 'Could not delete cost', type: 'error' }); }
  };

  const openHistoryModal = async (cost) => {
    setHistoryModal({ open: true, loading: true, data: null });
    try {
      const res = await api.get(`/operational_costs/${cost.id}/history`);
      setHistoryModal({ open: true, loading: false, data: res || res?.data });
    } catch (e) {
      console.error('Error fetching history:', e);
      setHistoryModal({ open: true, loading: false, data: { name: cost.name, status: cost.status, history: [] } });
    }
  };

  const openDocViewer = (cost) => {
    if (!cost.supporting_document_path) return;
    setDocModal({
      open: true,
      title: cost.name,
      url: cost.supporting_document_path
    });
  };

  const exportReportApi = (format) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        report_type: 'operational_cost',
        army_type_id: armyTypeId,
        format: format,
        auth_token: token || '',
        token: token || ''
      });
      window.open(`/api/v1/reports/payroll?${params}`, '_blank');
    } catch (e) {
      alert(`Error exporting ${format.toUpperCase()}`);
    }
  };

  const handlePrintReport = () => {
    window.print();
  };

  const filteredCosts = filter === 'all' ? costs : costs.filter(c => c.status === filter);
  const totalMonthly = costs.filter(c => c.frequency === 'monthly' && c.status === 'active').reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const totalActive = costs.filter(c => c.status === 'active').reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const pendingCount = costs.filter(c => c.status === 'pending').length;

  const msgBg = msg.type === 'error' ? '#fee2e2' : '#dcfce7';
  const msgColor = msg.type === 'error' ? '#991b1b' : '#166534';

  // Report modal calculations
  const reportFilteredCosts = costs.filter(c => {
    const matchesStatus = reportModal.filterStatus === 'all' || c.status === reportModal.filterStatus;
    const matchesFy = !reportModal.filterFy || (c.financial_year || '2026') === reportModal.filterFy;
    const matchesDept = !reportModal.filterDept || String(c.department_id) === String(reportModal.filterDept);
    return matchesStatus && matchesFy && matchesDept;
  });

  const reportTotalAmount = reportFilteredCosts.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const reportFixedTotal = reportFilteredCosts.filter(c => c.cost_type === 'fixed').reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const reportVariableTotal = reportFilteredCosts.filter(c => c.cost_type === 'variable').reduce((s, c) => s + parseFloat(c.amount || 0), 0);

  return (
    <div>
      {/* Top Action Bar & Summary Cards */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
          🏛️ Operational Costs — {armyTypeName}
        </h3>
        <button
          onClick={() => setReportModal({ open: true, filterStatus: 'all', filterFy: '2026', filterDept: '' })}
          style={{
            padding: '9px 18px', background: '#0f766e', color: '#fff', border: 'none',
            borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
            boxShadow: '0 2px 4px rgba(15,118,110,0.2)'
          }}
        >
          <span>📊</span> Operation Cost Report
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Active Costs', value: costs.filter(c => c.status === 'active').length, color: '#1b4332' },
          { label: 'Pending Approval', value: pendingCount, color: pendingCount > 0 ? '#854d0e' : '#475569' },
          { label: 'Monthly Total', value: `$${totalMonthly.toFixed(2)}`, color: '#0284c7' },
          { label: 'All Active Total', value: `$${totalActive.toFixed(2)}`, color: '#7c3aed' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Workflow Note */}
      <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#92400e' }}>
        <strong>Operational Cost Workflow:</strong> Submitting a transaction attaches Budget Code, Financial Year, Funding Source, and Supporting Docs. Finance Director / Accountant General approval activates the cost and records a permanent transaction audit trail.
      </div>

      {msg.text && <div style={{ marginBottom: 16, padding: '10px 14px', background: msgBg, borderRadius: 6, color: msgColor, fontSize: 13, fontWeight: 600 }}>{msg.text}</div>}

      {/* Add / Edit Form */}
      {canManage && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>{editing ? '✏️ Edit' : '➕ Add'} Operational Cost — {armyTypeName}</h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Cost Name */}
            <div style={{ flex: '2 1 180px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Cost Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Vehicle Fuel — March 2026"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>

            {/* Cost Type */}
            <div style={{ flex: '1 1 110px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Cost Type</label>
              <select value={form.cost_type} onChange={e => setForm({ ...form, cost_type: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                <option value="fixed">Fixed</option>
                <option value="variable">Variable</option>
              </select>
            </div>

            {/* Amount */}
            <div style={{ flex: '1 1 100px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Amount (USD) *</label>
              <input type="number" required min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>

            {/* Frequency */}
            <div style={{ flex: '1 1 110px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Frequency</label>
              <select value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="one_time">One-Time</option>
              </select>
            </div>

            {/* Department */}
            <div style={{ flex: '1 1 140px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Department</label>
              <select value={form.department_id} onChange={e => setForm({ ...form, department_id: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                <option value="">Force-wide</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            {/* Budget Code */}
            <div style={{ flex: '1 1 120px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Budget Code</label>
              <input value={form.budget_code || ''} onChange={e => setForm({ ...form, budget_code: e.target.value })} placeholder="e.g. BC-2026-01"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>

            {/* Financial Year */}
            <div style={{ flex: '1 1 100px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Financial Year</label>
              <input value={form.financial_year || ''} onChange={e => setForm({ ...form, financial_year: e.target.value })} placeholder="e.g. 2026"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>

            {/* Funding Source */}
            <div style={{ flex: '1 1 130px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Funding Source</label>
              <input value={form.funding_source || ''} onChange={e => setForm({ ...form, funding_source: e.target.value })} placeholder="e.g. State Treasury"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>

            {/* Supporting Document Upload Option */}
            <div style={{ flex: '3 1 240px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                📎 Support Document Upload
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    id="supporting-doc-file-input"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    onChange={handleFileUpload}
                    style={{
                      padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6,
                      fontSize: 12, background: '#f8fafc', width: '100%'
                    }}
                  />
                  {form.supporting_document_path && (
                    <button
                      type="button"
                      onClick={handleRemoveDoc}
                      style={{ padding: '6px 10px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {uploadingDoc && <span style={{ fontSize: 11, color: '#0284c7' }}>Reading document file...</span>}
                {form.supporting_document_path && (
                  <div style={{ fontSize: 11, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '4px 8px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>✅ Attached:</span>
                    <strong style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {docFileName || (form.supporting_document_path.startsWith('data:') ? 'Uploaded Document' : form.supporting_document_path)}
                    </strong>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div style={{ flex: '2 1 180px' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Description / Purpose</label>
              <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional notes"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }} />
            </div>

            {/* Submit / Cancel */}
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="submit" disabled={loading || uploadingDoc}
                style={{ padding: '9px 18px', background: '#1b4332', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                {loading ? 'Saving...' : editing ? 'Update Cost' : 'Submit for Approval'}
              </button>
              {editing && (
                <button type="button" onClick={() => {
                  setEditing(null);
                  setDocFileName('');
                  setForm({
                    name: '', cost_type: 'fixed', amount: '', frequency: 'monthly', department_id: '',
                    description: '', budget_code: '', financial_year: '2026', funding_source: '', supporting_document_path: ''
                  });
                }} style={{ padding: '9px 14px', background: '#e2e8f0', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 12, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', width: 'fit-content' }}>
        {[
          { key: 'all', label: 'All Costs' },
          { key: 'pending', label: '⏳ Pending' },
          { key: 'active', label: '✅ Active' },
          { key: 'inactive', label: '— Rejected' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{ padding: '6px 14px', border: 'none', background: filter === f.key ? '#1b4332' : '#fff', color: filter === f.key ? '#fff' : '#374151', fontWeight: filter === f.key ? 700 : 500, cursor: 'pointer', fontSize: 12 }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Costs Table */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Transaction Name & Budget', 'Type & Freq', 'Amount', 'FY & Source', 'Department', 'Support Document', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredCosts.length === 0 && <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>No operational costs recorded for this force.</td></tr>}
            {filteredCosts.map((c, i) => (
              <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                  <div style={{ color: '#0f172a' }}>{c.name}</div>
                  {c.budget_code && <div style={{ fontSize: 11, color: '#0284c7', fontWeight: 700 }}>Code: {c.budget_code}</div>}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, background: c.cost_type === 'fixed' ? '#eff6ff' : '#faf5ff', color: c.cost_type === 'fixed' ? '#1d4ed8' : '#6d28d9', display: 'inline-block', marginBottom: 2 }}>{c.cost_type}</span>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{c.frequency}</div>
                </td>
                <td style={{ padding: '10px 14px', fontWeight: 800, color: '#059669', fontSize: 14 }}>
                  ${parseFloat(c.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
                <td style={{ padding: '10px 14px', color: '#475569', fontSize: 12 }}>
                  <div>FY: <strong>{c.financial_year || '2026'}</strong></div>
                  <div style={{ color: '#64748b', fontSize: 11 }}>{c.funding_source || 'State Budget'}</div>
                </td>
                <td style={{ padding: '10px 14px', color: '#6b7280' }}>{c.department?.name || 'Force-wide'}</td>
                <td style={{ padding: '10px 14px' }}>
                  {c.supporting_document_path ? (
                    <button
                      onClick={() => openDocViewer(c)}
                      style={{
                        fontSize: 12, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe',
                        padding: '3px 8px', borderRadius: 4, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4
                      }}
                    >
                      📄 View Doc
                    </button>
                  ) : (
                    <span style={{ color: '#94a3b8', fontSize: 11 }}>None</span>
                  )}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <Badge status={c.status} />
                  {c.approved_by?.email && (
                    <div style={{ fontSize: 10, color: '#166534', marginTop: 3 }}>
                      By: {c.approved_by.email.split('@')[0]}
                    </div>
                  )}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {canApprove && c.status === 'pending' && (
                    <>
                      <button onClick={() => handleApprove(c.id, c.name)}
                        style={{ marginRight: 4, padding: '4px 10px', background: '#059669', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Approve</button>
                      <button onClick={() => handleReject(c.id, c.name)}
                        style={{ marginRight: 4, padding: '4px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>Reject</button>
                    </>
                  )}
                  {canManage && c.status !== 'active' && (
                    <button onClick={() => {
                      setEditing(c.id);
                      setForm({
                        name: c.name, cost_type: c.cost_type, amount: c.amount, frequency: c.frequency,
                        department_id: c.department_id || '', description: c.description || '',
                        budget_code: c.budget_code || '', financial_year: c.financial_year || '2026',
                        funding_source: c.funding_source || '', supporting_document_path: c.supporting_document_path || ''
                      });
                      setDocFileName('');
                    }} style={{ marginRight: 4, padding: '4px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Edit</button>
                  )}
                  <button onClick={() => openHistoryModal(c)}
                    style={{ marginRight: 4, padding: '4px 10px', background: '#475569', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                    📜 History
                  </button>
                  {(canManage && c.status === 'pending') && (
                    <button onClick={() => handleDelete(c.id)}
                      style={{ padding: '4px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Operation Cost Report Modal */}
      {reportModal.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '92%', maxWidth: 950, maxHeight: '90vh', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>
            
            {/* Header */}
            <div style={{ padding: '18px 24px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                  📊 Operation Cost Report — {armyTypeName}
                </h3>
                <div style={{ fontSize: 12, color: '#ccfbf1', marginTop: 2 }}>
                  Official Ministry of Finance & Armed Forces Financial Statement
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={handlePrintReport} style={{ padding: '7px 14px', background: '#374151', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                  🖨️ Print PDF
                </button>
                <button onClick={() => exportReportApi('excel')} style={{ padding: '7px 14px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                  📈 Excel
                </button>
                <button onClick={() => exportReportApi('csv')} style={{ padding: '7px 14px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                  📄 CSV
                </button>
                <button onClick={() => setReportModal({ ...reportModal, open: false })} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', marginLeft: 8 }}>✕</button>
              </div>
            </div>

            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
              {/* Report Filters */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 20, background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>Filter Report Scope:</div>
                <div>
                  <select
                    value={reportModal.filterStatus}
                    onChange={e => setReportModal({ ...reportModal, filterStatus: e.target.value })}
                    style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active Only</option>
                    <option value="pending">Pending Only</option>
                    <option value="inactive">Rejected / Inactive</option>
                  </select>
                </div>
                <div>
                  <select
                    value={reportModal.filterFy}
                    onChange={e => setReportModal({ ...reportModal, filterFy: e.target.value })}
                    style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                  >
                    <option value="">All Financial Years</option>
                    <option value="2026">FY 2026</option>
                    <option value="2025">FY 2025</option>
                  </select>
                </div>
                <div>
                  <select
                    value={reportModal.filterDept}
                    onChange={e => setReportModal({ ...reportModal, filterDept: e.target.value })}
                    style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}
                  >
                    <option value="">All Departments</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>

              {/* KPI Breakdown */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: 14, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>Total Items</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d', marginTop: 4 }}>{reportFilteredCosts.length}</div>
                </div>
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: 14, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#1e40af', fontWeight: 600 }}>Fixed Cost Total</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#1d4ed8', marginTop: 4 }}>${reportFixedTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                </div>
                <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', padding: 14, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#6b21a8', fontWeight: 600 }}>Variable Cost Total</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#7e22ce', marginTop: 4 }}>${reportVariableTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                </div>
                <div style={{ background: '#fefce8', border: '1px solid #fef08a', padding: 14, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: '#854d0e', fontWeight: 600 }}>Total Operational Expense</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#a16207', marginTop: 4 }}>${reportTotalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>

              {/* Printable Table */}
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#0f766e', color: '#fff' }}>
                      {['#', 'Budget Code', 'Cost Name', 'Department', 'Type', 'Freq', 'FY', 'Funding Source', 'Amount (USD)', 'Doc', 'Status'].map(h => (
                        <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontWeight: 700, fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportFilteredCosts.length === 0 ? (
                      <tr><td colSpan={11} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No operational cost records found matching report filters.</td></tr>
                    ) : (
                      reportFilteredCosts.map((c, i) => (
                        <tr key={c.id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 10px', fontWeight: 700, color: '#64748b' }}>{i + 1}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 700, color: '#0284c7' }}>{c.budget_code || '—'}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 600, color: '#0f172a' }}>{c.name}</td>
                          <td style={{ padding: '8px 10px', color: '#475569' }}>{c.department?.name || 'Force-wide'}</td>
                          <td style={{ padding: '8px 10px', textTransform: 'capitalize' }}>{c.cost_type || 'Fixed'}</td>
                          <td style={{ padding: '8px 10px', textTransform: 'capitalize' }}>{c.frequency || 'Monthly'}</td>
                          <td style={{ padding: '8px 10px' }}>{c.financial_year || '2026'}</td>
                          <td style={{ padding: '8px 10px', color: '#475569' }}>{c.funding_source || 'State Budget'}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 800, color: '#059669' }}>${parseFloat(c.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '8px 10px' }}>{c.supporting_document_path ? '📎 Yes' : '—'}</td>
                          <td style={{ padding: '8px 10px' }}><Badge status={c.status} /></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {reportFilteredCosts.length > 0 && (
                    <tfoot>
                      <tr style={{ background: '#f1f5f9', fontWeight: 800 }}>
                        <td colSpan={8} style={{ padding: '10px', textAlign: 'right', fontSize: 12 }}>TOTAL REPORT EXPENDITURE:</td>
                        <td style={{ padding: '10px', color: '#059669', fontSize: 14 }}>${reportTotalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            <div style={{ padding: '12px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <button onClick={() => setReportModal({ ...reportModal, open: false })}
                style={{ padding: '7px 18px', background: '#475569', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                Close Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {docModal.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.75)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, width: '85%', maxWidth: 800, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '14px 20px', background: '#1e293b', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>📄 Supporting Document — {docModal.title}</h4>
              <button onClick={() => setDocModal({ open: false, title: '', url: '' })} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: 20, flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f1f5f9' }}>
              {docModal.url.startsWith('data:image') || docModal.url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <img src={docModal.url} alt="Support Document" style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: 6 }} />
              ) : docModal.url.startsWith('data:application/pdf') || docModal.url.match(/\.pdf$/i) ? (
                <iframe src={docModal.url} title="PDF Viewer" style={{ width: '100%', height: '60vh', border: 'none', borderRadius: 6 }} />
              ) : (
                <div style={{ textAlign: 'center', padding: 30 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📎</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Attached Supporting File</div>
                  <a href={docModal.url} download={`support_doc_${docModal.title}.pdf`} target="_blank" rel="noreferrer" style={{ padding: '8px 16px', background: '#1d4ed8', color: '#fff', textDecoration: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13 }}>
                    📥 Download / Open Document
                  </a>
                </div>
              )}
            </div>
            <div style={{ padding: '10px 20px', background: '#fff', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <button onClick={() => setDocModal({ open: false, title: '', url: '' })} style={{ padding: '6px 16px', background: '#475569', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Approval & Audit History Modal */}
      {historyModal.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, width: '90%', maxWidth: 650, overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ padding: '16px 20px', background: '#0f172a', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                📜 Transaction Approval History — {historyModal.data?.name || 'Cost Transaction'}
              </h3>
              <button onClick={() => setHistoryModal({ open: false, loading: false, data: null })}
                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ padding: 20, maxHeight: '70vh', overflowY: 'auto' }}>
              {historyModal.loading ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>Loading transaction history...</div>
              ) : (
                <div>
                  <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}>
                    <div><strong>Submitted By:</strong> {historyModal.data?.created_by || 'Finance Officer'}</div>
                    {historyModal.data?.approved_by && (
                      <div style={{ marginTop: 4 }}>
                        <strong>Approved By:</strong> {historyModal.data.approved_by} on {historyModal.data.approved_at ? new Date(historyModal.data.approved_at).toLocaleString() : 'N/A'}
                      </div>
                    )}
                    <div style={{ marginTop: 4 }}><strong>Current Status:</strong> <Badge status={historyModal.data?.status} /></div>
                  </div>

                  <h4 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 10px', color: '#1e293b' }}>Audit Logs ({historyModal.data?.history?.length || 0})</h4>

                  {(!historyModal.data?.history || historyModal.data.history.length === 0) ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', border: '1px dashed #cbd5e1', borderRadius: 6 }}>
                      No audit history recorded yet for this transaction.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {historyModal.data.history.map((log) => {
                        let details = {};
                        try { details = JSON.parse(log.change_data || '{}'); } catch(e){}
                        return (
                          <div key={log.id} style={{ padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fafafa' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', color: log.action_type === 'approve_action' ? '#059669' : log.action_type === 'reject_action' ? '#dc2626' : '#0284c7' }}>
                                {log.action_type?.replace('_action', '') || 'action'}
                              </span>
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                {new Date(log.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: '#334155' }}>
                              User: <strong>{log.user?.email || 'System User'}</strong>
                            </div>
                            {details.reason && (
                              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4, background: '#fef2f2', padding: '4px 8px', borderRadius: 4 }}>
                                Reason: {details.reason}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <button onClick={() => setHistoryModal({ open: false, loading: false, data: null })}
                style={{ padding: '6px 16px', background: '#475569', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperationalCostsTab;
